import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Button, Modal, ModalHeader, ModalBody } from '@doxyme/ui';
import _ from 'lodash';
import { BackButton } from '../../../../components/back-button';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Link } from '../../../../components/navigation';
import { is } from 'ramda';
import classnames from 'classnames';
import { ExtensionTypes, selectInstitution, selectUser, } from '../../../../store/features/system';
import { selectExtension, selectExtensionsList } from '../../selectors';
import { uninstallExtension, installExtension, clearExtensionReducer, getExtensionById, addAwareness, updatePublicAttributes, marketplaceExtensionClosed, } from '../../actions';
import { FormattedMessage } from 'react-intl';
import ReactMarkdown from 'react-markdown';
import './extension-details.scss';
import { ExtensionActionButton } from './extension-details-action-buttons';
import { addToast } from '../../../core/actions';
import { ToastType } from '../../../core';
import { nativeExtensionsListWithSettings } from '../../../core/constants/extension';
import { shouldHideExtensionSettinsForNotClinic, isExtensionInstalled } from '../../../core/helpers/extension';
import { AvailableFor } from '../../../marketplace-admin/constants';
const YOUTUBE_HOST = 'https://www.youtube.com/';
const DOXY_HELP_URL = 'https://help.doxy.me/en';
function testIsUTube(link) {
    return Boolean(is(String, link)) && link.startsWith(YOUTUBE_HOST);
}
function cn(name) {
    return `ExtensionDetails__${name}`;
}
const BILLING_TYPE_LOCALES = {
    PER_MINUTE: {
        title: 'account.billings.per_minute',
    },
    PER_HOUR: {
        title: 'account.billings.per_hour',
    },
    MONTHLY: {
        title: 'account.billings.per_month',
    },
    FREE: {
        title: 'account.billings.free',
    },
    FREE_FOR_PRO: {
        title: 'account.billings.free_for_pro',
    },
};
const DEFAULT_SCROLL_BOUND = 170;
const MIN_MOBILE_WIDTH = 860;
const MOBILE_HEADER_HEIGHT = 56;
const MarkdownLinkRenderer = (props) => {
    return (<a href={props.href} target="_blank" rel="noreferrer">
      {props.children}
    </a>);
};
export const ExtensionDetails = () => {
    const { id: idString } = useParams();
    const [isOpenTerms, setIsOpenTerms] = useState(false);
    const dispatch = useDispatch();
    const extension = useSelector(selectExtension);
    const extensions = useSelector(selectExtensionsList);
    const [inFlight, setInFlight] = useState(false);
    const [topScrollBound, setTopScrollBound] = useState(DEFAULT_SCROLL_BOUND);
    const institution = useSelector(selectInstitution);
    const user = useSelector(selectUser);
    const asideRef = useRef(null);
    const mainBlockRef = useRef(null);
    const handleChangeStatus = useCallback((type) => {
        return () => {
            try {
                setInFlight(true);
                if (institution?.institutionAccess.hasOwnProperty(`extensions_${extension.name}`)) {
                    const institutionData = { ...institution };
                    if (type === ExtensionTypes.uninstalled) {
                        institutionData.institutionAccess[`extensions_${extension.name}`] = false;
                    }
                    if (type === ExtensionTypes.installed) {
                        institutionData.institutionAccess[`extensions_${extension.name}`] = true;
                    }
                    dispatch(updatePublicAttributes(institutionData));
                }
                else {
                    if (type === ExtensionTypes.uninstalled) {
                        dispatch(uninstallExtension(extension));
                    }
                    if (type === ExtensionTypes.installed) {
                        dispatch(installExtension(extension));
                    }
                }
                setIsOpenTerms(false);
            }
            catch (error) {
                dispatch(addToast(ToastType.error, error.message, 3000));
            }
            finally {
                setInFlight(false);
            }
        };
    }, [extension, institution, dispatch]);
    useEffect(() => {
        return () => {
            if (extension) {
                dispatch(marketplaceExtensionClosed(extension));
            }
        };
    }, [extension, dispatch]);
    useEffect(() => {
        return () => {
            dispatch(clearExtensionReducer());
        };
    }, [dispatch]);
    useEffect(() => {
        dispatch(getExtensionById(idString));
    }, [idString, dispatch]);
    useEffect(() => {
        if (extensions.length > 0 && extension) {
            const shouldUpdateAwareness = extensions.some((ext) => ext.id === extension.id && ext.isNew);
            if (shouldUpdateAwareness) {
                dispatch(addAwareness(idString));
            }
        }
    }, [dispatch, extensions, extension, idString]);
    useEffect(() => {
        const onPageScroll = _.throttle(() => {
            const scrollTop = window.scrollY;
            if (scrollTop >= topScrollBound && window.outerWidth > MIN_MOBILE_WIDTH
                || scrollTop > topScrollBound - MOBILE_HEADER_HEIGHT && window.innerWidth <= MIN_MOBILE_WIDTH) {
                asideRef.current.classList.add('ExtensionDetails__aside-inner--fixed');
            }
            else {
                asideRef.current.classList.remove('ExtensionDetails__aside-inner--fixed');
            }
        }, 50);
        window.addEventListener('scroll', onPageScroll);
        return () => {
            window.removeEventListener('scroll', onPageScroll);
        };
    }, [topScrollBound]);
    useEffect(() => {
        if (mainBlockRef.current) {
            setTopScrollBound(mainBlockRef.current.getBoundingClientRect().top);
        }
    }, [extension]);
    const mediaList = extension
        ? [
            {
                link: extension.video,
                title: '',
            },
        ]
        : [];
    return (<div className={cn('container')}>
      {extension && (<Modal isOpen={isOpenTerms} closeModal={() => setIsOpenTerms(false)}>
          <ModalHeader onClose={() => setIsOpenTerms(false)}>
            <h4>
              Accept {extension.title}’s{` `}
              <FormattedMessage id="common.terms_and_conditions"/>
            </h4>
          </ModalHeader>
          <ModalBody>
            <div className={cn('terms')}>
              <div className={cn('terms-text')}>
                By clicking{` `}
                <strong>
                  &apos;
                  <FormattedMessage id="common.continue"/>
                  &apos;
                </strong>
                {` `}
                you are agreeing to {extension.title}’s{` `}
                <br />
                <a href={extension.termsUrl ?? '#'} target="_blank" rel="noreferrer">
                  <FormattedMessage id="common.terms_and_conditions"/>.
                </a>
              </div>
              <div className={cn('terms-actions')}>
                <Button size="medium" btnStyle="outline" color="secondary" onClick={() => setIsOpenTerms(false)}>
                  <FormattedMessage id="common.cancel"/>
                </Button>
                <Button btnStyle="solid" size="medium" data-testid="continue-install-btn" onClick={handleChangeStatus(ExtensionTypes.installed)}>
                  <FormattedMessage id="common.continue"/>
                </Button>
              </div>
            </div>
          </ModalBody>
        </Modal>)}
      <div>
        <BackButton href="/account/extensions">
          <FormattedMessage id="account.marketplace.back-to-extensions"/>
        </BackButton>
      </div>
      {extension && (<>
          <div className={cn('header')}>
            <div className={cn('header-row')}>
              <div className={cn('logo')}>
                <img src={extension.icon} alt={extension.title}/>
              </div>
              <div className={cn('h-group')}>
                <h2 className={cn('title')}>{extension.title}</h2>
                <ReactMarkdown>{extension.shortDescription}</ReactMarkdown>
              </div>
            </div>
          </div>
          <div ref={mainBlockRef} className={cn('main')}>
            <div className={cn('content')}>
              <div className={cn('media-block')}>
                {mediaList.map((media) => (<div key={media.link} className={cn('media')}>
                    {testIsUTube(media.link) ? (<div className={cn('video-wrap')}>
                        <iframe src={media.link} frameBorder="0" allowFullScreen/>
                      </div>) : (<img src={media.link} alt={media.title}/>)}
                  </div>))}
              </div>
              <div className={cn('details')}>
                <div className={cn('info')}>
                  <ReactMarkdown components={{ a: MarkdownLinkRenderer }}>
                    {extension.description}
                  </ReactMarkdown>
                </div>
                {extension.optionalDescription != null && (<div className={cn('info')}>
                    <ReactMarkdown components={{ a: MarkdownLinkRenderer }}>
                      {extension.optionalDescription}
                    </ReactMarkdown>
                  </div>)}
              </div>
            </div>
            <div className={cn('aside')}>
              <div ref={asideRef} className={cn('aside-inner')}>
                <div className={cn('aside-group')}>
                  <div className={cn('actions')}>
                    <ExtensionActionButton inFlight={inFlight} handleChangeStatus={handleChangeStatus} setIsOpenTerms={setIsOpenTerms}/>

                    {Boolean(extension.hint) && (<span className={cn('hint')}>{extension.hint}</span>)}
                  </div>
                </div>
                <div className={cn('aside-group')}>
                  <h6 className={cn('aside-heading')}>
                    <FormattedMessage id="account.billings.price"/>
                  </h6>
                  {(extension.tariff === 'FREE' || !extension.tariff) && (<div className={cn('aside-link')}>
                      <FormattedMessage id={extension.availableFor === AvailableFor.FREE
                    ? BILLING_TYPE_LOCALES.FREE.title
                    : BILLING_TYPE_LOCALES.FREE_FOR_PRO.title}/>
                    </div>)}
                  {extension.tariff !== 'FREE' && extension.tariff && (<div className={classnames(cn('aside-link'), 'is-lowercase')}>
                      ${extension.price}
                      {` `}
                      {Boolean(extension.tariff) && (<FormattedMessage id={BILLING_TYPE_LOCALES[extension.tariff].title}/>)}
                      <div className={cn('aside-hint')}>
                        <FormattedMessage id="account.billings.billed_monthly"/>
                      </div>
                    </div>)}
                </div>
                <div className={cn('aside-group')}>
                  <h6 className={cn('aside-heading')}>
                    <FormattedMessage id="account.marketplace.publisher"/>
                  </h6>
                  <div className={cn('aside-link')}>{extension.source}</div>
                  {extension.email && (<div className={cn('aside-link')}>
                      <a href={`mailto:${extension.email}`}>{extension.email}</a>
                    </div>)}
                  {extension.termsUrl && (<div className={cn('aside-link')}>
                      <a href={extension.termsUrl} target="_blank" rel="noreferrer">
                        <FormattedMessage id="common.terms_and_conditions"/>
                      </a>
                    </div>)}
                </div>
                {isExtensionInstalled(extension, user, institution) &&
                nativeExtensionsListWithSettings.includes(extension.name) &&
                !shouldHideExtensionSettinsForNotClinic(extension, institution) && (<div className={cn('aside-group')}>
                      <h6 className={cn('aside-heading')}>
                        <FormattedMessage id="account.marketplace.settings"/>
                      </h6>
                      <div className={cn('aside-link')}>
                        <Link to={`/account/settings/extensions?panel=${extension.name}`}>
                          <FormattedMessage id="account.marketplace.settings-panel" values={{ extensionTitle: extension.title }}/>
                        </Link>
                      </div>
                    </div>)}
                <div className={cn('aside-group')}>
                  <h6 className={cn('aside-heading')}>
                    <FormattedMessage id="common.doxyme_support"/>
                  </h6>
                  <div className={cn('aside-link')}>
                    <a href={extension.supportArticleLink ?? DOXY_HELP_URL} target="_blank" rel="noreferrer">
                      {extension.supportArticleText ?? (<FormattedMessage id="account.marketplace.how_to_use"/>)}
                    </a>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </>)}
    </div>);
};
